// Angular imports
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

// 3rd party imports
import { DxValidationGroupComponent } from 'devextreme-angular';
import { TextBoxType } from 'devextreme/ui/text_box';

// CC-Common imports
import { IconConstants } from '@therealmanage/ciranet-ui-common';
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Component imports
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { EnvironmentConfigService } from '../../_services/environment-config.service';

// Data Imports
import { LoginParameterDto } from '../../_models/login-parameter-dto';
import { CustomAnalyticsService } from 'src/app/_services/shared-services/analytics.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent extends RecaptchaBaseComponent implements OnInit {
  @ViewChild('loginValidationGroup', { static: false })
  loginValidationGroup: DxValidationGroupComponent;

  loginAttempts = 0;
  noOfAllowedAttempts = 5;
  returnUrl: string;
  loadingVisible = false;
  isSmallScreen = false;
  btnLoginAndResetWidth = 132;
  passwordMode: TextBoxType;
  passwordShowButton: object;
  passwordHideButton: object;
  loginParam: LoginParameterDto;
  showRetrieveUsername = false;
  showForgotPassword = false;
  showRegister = false;
  showErrorMessage = false;
  errorMessage = '';
  showGoogleAuth = true;

  loginIcon = IconConstants.LOGIN;
  resetIcon = IconConstants.RESET;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private screenService: ScreenService,
    private helperService: HelperService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService,
    private analyticsService: CustomAnalyticsService
  ) {
    super(errorService, envConfig);
    this.passwordMode = 'password';
    this.passwordShowButton = {
      icon: 'fa-solid fa-eye',
      type: 'default',
      stylingMode: 'text',
      hint: 'Show Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.passwordHideButton = {
      icon: 'fa-solid fa-eye-slash',
      type: 'default',
      stylingMode: 'text',
      hint: 'Hide Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
  }

  ngOnInit() {
    RxjsUtils.subscribeToQueryParams(this.route, (params) => {
      this.returnUrl = this.route.snapshot.queryParams['returnUrl'];

      // Handle Google SSO error
      this.handleGoogleSSOError();

      // Check if returning from Google OAuth registration
      const fromGoogle = this.route.snapshot.queryParams['fromGoogle'];
      if (fromGoogle === 'true') {
        this.showRegister = true;
        this.helperService.subjectLoginHeaderText.next('Register Account');
      }
    });
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );
    this.loginParam = this.authService.getRemembered();
  }

  private handleGoogleSSOError(): void {
    const error = this.route.snapshot.queryParams['error'];
    if (error !== 'google_sso_failed') {
      return;
    }

    const message = this.route.snapshot.queryParams['message'];
    this.showErrorMessage = true;
    this.errorMessage = message || 'Google sign-in failed. Please try again.';
  }

  btnLoginClick(event: any): void {
    // Clear any previous error messages
    this.showErrorMessage = false;
    this.errorMessage = '';

    const validator = this.loginValidationGroup.instance.validate();
    if (validator.isValid) {
      if (
        this.loginAttempts < this.noOfAllowedAttempts ||
        this.isValidRecaptcha()
      ) {
        this.loginParam.recaptchaToken = this.recaptchaToken;
        this.doLogin().then((success: boolean) => {
          if (success) {
            this.loginAttempts = 0;
          } else {
            this.loginAttempts += 1;
            this.resetRecaptcha();
          }
        });
      }
    }
  }

  btnResetPasswordClick(event: Event): void {
    event.preventDefault();
    this.analyticsService.trackEvent('reset_password', {
      action: 'initiated'
    });
    this.showForgotPassword = !this.showForgotPassword;

    setTimeout(() => {
      if (this.showForgotPassword) {
        this.helperService.subjectLoginHeaderText.next('Reset Password');
        this.helperService.subjectIsRMLogoHidden.next(true);
      } else {
        this.helperService.subjectLoginHeaderText.next('Welcome Home!');
        this.helperService.subjectIsRMLogoHidden.next(false);
      }
    }, 0);
  }

  btnMakePaymentClick(event: Event): void {
    this.analyticsService.trackEvent('quick_payment', {
      action: 'initiated'
    });
    this.router.navigate(['user', 'quickpayment']);
  }

  btnGoogleLoginClick(event: Event): void {
    this.analyticsService.trackEvent('google_login', {
      action: 'initiated'
    });

    const returnUrl = this.returnUrl || '/home';
    const spaOrigin = window.location.origin;
    const googleLoginUrl = `${this.envConfig.ccAuthenticationApiUrl}google-login?portalType=RP&returnUrl=${encodeURIComponent(returnUrl)}&spaOrigin=${encodeURIComponent(spaOrigin)}`;
    window.location.href = googleLoginUrl;
  }

  btnRetrieveUsernameClick(event: Event): void {
    event.preventDefault();
    this.analyticsService.trackEvent('retrieve_username', {
      action: 'initiated'
    });
    this.showRetrieveUsername = !this.showRetrieveUsername;

    setTimeout(() => {
      if (this.showRetrieveUsername) {
        this.helperService.subjectLoginHeaderText.next(
          'Retrieve Username or Validate Email Address'
        );
        this.helperService.subjectIsRMLogoHidden.next(true);
      } else {
        this.helperService.subjectLoginHeaderText.next('Welcome Home!');
        this.helperService.subjectIsRMLogoHidden.next(false);
      }
    }, 0);
  }

  onClosePanelClick(): void {
    this.showRetrieveUsername = false;
    setTimeout(() => {
      this.helperService.subjectLoginHeaderText.next('Welcome Home!');
      this.helperService.subjectIsRMLogoHidden.next(false);
    }, 0);
  }

  onCloseForgotPasswordPanelClick(): void {
    this.showForgotPassword = false;
    setTimeout(() => {
      this.helperService.subjectLoginHeaderText.next('Welcome Home!');
      this.helperService.subjectIsRMLogoHidden.next(false);
    }, 0);
  }

  btnRegisterNowClick(event: Event): void {
    event.preventDefault();
    this.analyticsService.trackEvent('register_account', {
      action: 'initiated'
    });
    this.showRegister = !this.showRegister;

    setTimeout(() => {
      if (this.showRegister) {
        this.helperService.subjectLoginHeaderText.next('Register Account');
        this.helperService.subjectIsRMLogoHidden.next(true);
      } else {
        this.helperService.subjectLoginHeaderText.next('Welcome Home!');
        this.helperService.subjectIsRMLogoHidden.next(false);
      }
    }, 0);
  }

  onCloseRegisterPanelClick(): void {
    this.showRegister = false;
    setTimeout(() => {
      this.helperService.subjectLoginHeaderText.next('Welcome Home!');
      this.helperService.subjectIsRMLogoHidden.next(false);
    }, 0);
  }

  linkChooseDifferentPortalClick() {
    window.location.href = this.envConfig.portalSelectionUrl;
  }

  private doLogin(): Promise<boolean> {
    return new Promise((resolve) => {
      this.loadingVisible = true;
      this.authService.clearAuthLocalStorageData();

      RxjsUtils.safeSubscribe(
        this.authService.login(this.loginParam),
        (next) => {
          this.loadingVisible = false;
          this.authService.updateSubjectLoginSuccess(true);

          if (next && next.userId) {
            this.analyticsService.setUserId(next.userId);
          }

          this.navigateAfterLoginSuccess();
          this.analyticsService.trackEvent('login_success', {
            method: 'password'
          });
          resolve(true);
        },
        (error) => {
          this.loadingVisible = false;
          if (error.status === 404) {
            this.handleLoginFailed(error);
          } else {
            this.processApiError(error);
          }
          this.analyticsService.trackEvent('login_failed', {
            method: 'password',
            error_message: error.error.Message,
            error_reasonCode: error.error.ReasonCode // Send a relevant error message
          });
          resolve(false);
        }
      );
    });
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }

  private navigateAfterLoginSuccess(): void {
    if (!!this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      this.helperService.subjectAutoSelectHomeMenuItem.next(this.returnUrl);
    } else {
      this.router.navigate(['/home']);
      this.helperService.subjectAutoSelectHomeMenuItem.next('/home');
    }
  }

  private handleLoginFailed(error: any) {
    let message = 'Login failed, please try again.';
    if (!!error.error && !!error.error.Message) {
      message = error.error.Message;
    }

    this.showErrorMessage = true;
    this.errorMessage = message;
  }
}
